import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Smartphone, Code2, Monitor, Apple, Layers, Boxes } from "lucide-react";

const OFFICIAL = [
  { name: "Android", href: "/android", internal: true, icon: Smartphone },
  { name: "JavaScript", href: "/javascript", internal: true, icon: Code2 },
  { name: "Windows", href: "/windows", internal: true, icon: Monitor },
  { name: "iOS", href: "/ios", internal: true, icon: Apple },
];

const UNOFFICIAL = [
  { name: "Cordova", href: "/cordova", internal: true, icon: Layers },
  { name: "Xamarin", href: "/xamarin", internal: true, icon: Boxes },
];

export default function DevelopmentKits() {
  return (
    <div>
      <section className="bg-[#f4f5f7] py-14 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="text-4xl font-semibold text-slate-800">Development Kits</h1>
        </div>
      </section>

      <article className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
        <Link
          to="/resources"
          className="inline-flex items-center gap-1 text-sm text-[#29abe2] hover:underline mb-8"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Resources
        </Link>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Overview</h2>
          <p className="text-slate-700 leading-relaxed text-[16px]">
            Originally developed for the TappyBLE Bluetooth-connected readers, all of our next-generation Tappy
            readers make use of a custom communication protocol called the Tappy Communication Messaging Protocol
            (TCMP). This command-based protocol is designed to provide a declarative way to asynchronously perform
            NFC operations. Although there are TapTrack-authored open source SDKs that abstract away the details of
            the protocol, if you are interested in the underlying protocol, feel free to read the{" "}
            <a
              href="https://docs.google.com/document/d/e/2PACX-1vTnzSyisbi6jD9mepVJsbgf6JL_qWjiIjGGIthRpULt2C5kkpuchm0nYa0U5NSgFOcpzzYzf6S9mGS6/pub?embedded=true"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#29abe2] hover:underline font-medium"
            >
              documentation
            </a>
            .
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-6">Official</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {OFFICIAL.map((sdk) => (
              <SdkCard key={sdk.name} sdk={sdk} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-6">Unofficial</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {UNOFFICIAL.map((sdk) => (
              <SdkCard key={sdk.name} sdk={sdk} />
            ))}
          </div>
        </section>
      </article>
    </div>
  );
}

function SdkCard({ sdk }) {
  const Icon = sdk.icon;
  const content = (
    <>
      <div className="w-16 h-16 rounded-full bg-[#29abe2]/10 flex items-center justify-center group-hover:bg-[#29abe2]/20 transition-colors">
        <Icon className="w-7 h-7 text-[#29abe2]" />
      </div>
      <h3 className="text-lg font-semibold text-slate-800 group-hover:text-[#29abe2] transition-colors">
        {sdk.name}
      </h3>
    </>
  );

  const className =
    "group flex flex-col items-center justify-center gap-4 bg-white border border-slate-200 rounded-md p-6 hover:border-[#29abe2] hover:shadow-md transition-all text-center";

  if (sdk.internal) {
    return (
      <Link to={sdk.href} className={className}>
        {content}
      </Link>
    );
  }
  return (
    <a href={sdk.href} target="_blank" rel="noopener noreferrer" className={className}>
      {content}
    </a>
  );
}
